"use client";

// Small shared shells for the organizer console. Nothing here decides anything
// - they exist so seven panels do not each restate the same card, button and
// error-note markup.

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";

export function PanelCard({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-[rgb(var(--border))] bg-white p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-neutral-900">{title}</h2>
          {description ? (
            <p className="mt-1 text-xs text-[rgb(var(--muted-foreground))]">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  tone?: "primary" | "neutral" | "danger";
};

export function ActionButton({
  loading,
  tone = "neutral",
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || loading}
      className={cn(
        "inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        tone === "primary" && "bg-[#E94D1D] text-white hover:bg-[#d44119]",
        tone === "neutral" &&
          "border border-[rgb(var(--border))] bg-white text-neutral-700 hover:bg-neutral-50",
        tone === "danger" && "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
        className,
      )}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
      {children}
    </button>
  );
}

/**
 * `alert` makes the note a live region, for a refusal that sits beside the
 * control it came from (`EX-COL07`: the staff box). Off by default, so the
 * panels that already render one keep their markup.
 */
export function ErrorNote({ message, alert = false }: { message: string | null; alert?: boolean }) {
  if (!message) return null;
  return (
    <p
      className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
      role={alert ? "alert" : undefined}
    >
      {message}
    </p>
  );
}

/** `status` is the `ErrorNote` `alert` for good news: announced politely, not as an interruption. */
export function SuccessNote({ message, status = false }: { message: string | null; status?: boolean }) {
  if (!message) return null;
  return (
    <p
      className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700"
      role={status ? "status" : undefined}
    >
      {message}
    </p>
  );
}

/** The server's own words when it refuses - never a guess at what it meant. */
export function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export const inputClass =
  "h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-white px-3 text-sm text-neutral-800 outline-none focus:border-[#E94D1D]";

export const selectClass =
  "h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-white px-2.5 text-sm text-neutral-800";

export const labelClass = "block text-xs font-semibold text-neutral-600";
